import { format, parseISO } from 'date-fns';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

import { Text } from '../../components';
import { Color } from '../../constants';
import { Model } from '../../models';
import { values } from '../../util';
import * as formatters from './formatters';
import useDimens from './useDimens';

type Props = Readonly<{
  kpi: Model.Kpi.View;
}>;

export default function KpiFull({ kpi }: Props) {
  const { title, data } = kpi;
  const { measuredEl, width } = useDimens();
  const [hover, setHover] = React.useState<string | null>(null);

  const formatter =
    (formatters as Record<string, (v: number) => string>)[kpi.formatter] ||
    formatters.standard;

  const dataValues = values(data).filter((v): v is number => v != null);
  if (dataValues.length < 1) return null;
  const mappedData = Object.entries(data).map(([k, v]) => ({
    x: k,
    y: v as number,
  }));

  const first = dataValues[0]!;
  const last = dataValues[dataValues.length - 1]!;
  const diff = first != last ? ((last - first) / first) * 100 : 0;

  const headline = kpi.aggregate ? dataValues.reduce((a, b) => a + b, 0) : last;

  const max = Math.max(...dataValues);
  const min = Math.min(...dataValues);

  const baseLabelStyle = {
    fill: '#7171a6',
    fontFamily: 'Inter, sans-serif',
    padding: 10,
    stroke: 'transparent',
  } as const;

  return (
    <html.div style={styles.container}>
      <html.div style={styles.rowTop}>
        <Text style={styles.title}>{title}</Text>
        <Text as="p" style={styles.value}>
          {formatter(headline)}
        </Text>
        {!kpi.aggregate && diff != 0 && (
          <Text style={styles.diffText(diff < 0 ? 'neg' : 'pos')}>
            <Text
              as="em"
              italic
              style={styles.diffIcon(diff < 0 ? 'neg' : 'pos')}
            >
              {diff < 0 ? '↓' : '↑'}
            </Text>
            {diff.toFixed(1)}%
          </Text>
        )}
      </html.div>
      <html.div ref={measuredEl}>
        <VictoryChart
          containerComponent={
            <VictoryVoronoiContainer
              labels={({ datum }) =>
                `${format(parseISO(datum.x), "d MMM ''yy")}\n \n${formatter(
                  datum.y
                )}`
              }
              labelComponent={<VictoryTooltip />}
              voronoiBlacklist={['area', 'line']}
              responsive={false}
              onActivated={(points) => setHover(points[0].x)}
              /* onDeactivated={points =>
              setTimeout(() => {
                if (points[0].x === hover) setHover(null);
              }, 100)
            } */
            />
          }
          width={width}
          height={420}
          domainPadding={{ x: 0, y: [50, 30] }}
          theme={{
            area: {
              style: {
                data: {
                  // fill: '#eff2ff',
                  /* fill: desaturate(
                    0.5,
                    transparentize(0.8, Color.primaryVibrant)
                  ), */
                  fill: 'rgba(136, 148, 215, 0.2)',
                },
              },
            },
            axis: {
              style: {
                axis: {
                  stroke: 'none',
                },
                grid: {
                  stroke: 'none',
                },
                tickLabels: baseLabelStyle,
              },
            },
            line: {
              style: {
                data: {
                  stroke: Color.primaryVibrant,
                  strokeWidth: 5,
                },
              },
            },
            scatter: {
              style: {
                data: { strokeWidth: 2, stroke: '#6078ff', fill: '#ffffff' },
              },
            },
            tooltip: {
              cornerRadius: 3,
              pointerLength: 20,
            },
            voronoi: {
              style: {
                labels: {
                  ...baseLabelStyle,
                  textAnchor: 'middle',
                  padding: 40,
                  pointerEvents: 'none',
                },
                flyout: {
                  stroke: '#ddd',
                  strokeWidth: 1,
                  fill: '#fff',
                  pointerEvents: 'none',
                },
              },
            },
          }}
        >
          <VictoryAxis
            // tickValues specifies both the number of ticks and where
            // they are placed on the axis
            tickFormat={(x) => format(parseISO(x), 'd MMM')}
            tickCount={10}
          />
          <VictoryAxis dependentAxis style={{ grid: { stroke: '#eee' } }} />
          <VictoryArea
            name="area"
            data={mappedData}
            domain={{ x: undefined, y: [min, max] }}
          />
          <VictoryLine name="line" data={mappedData} />
          <VictoryScatter
            data={mappedData}
            size={({ datum }) => (datum.x === hover ? 8 : 0)}
          />
        </VictoryChart>
      </html.div>
    </html.div>
  );
}

const styles = css.create({
  container: {
    marginBottom: 20,
    backgroundColor: '#fff',
  },
  rowTop: {
    paddingTop: 25,
    paddingInline: 30,
    paddingBottom: 0,
  },
  title: {
    fontSize: 20,
  },
  value: {
    paddingTop: 10,
    paddingInline: 0,
    paddingBottom: 10,
    fontSize: 30,
    color: '#5742e4',
    fontWeight: '600',
  },
  diffText: (kind: 'neg' | 'pos') => ({
    fontSize: 20,
    color: kind === 'neg' ? '#e84c85' : '#12c457',
  }),
  diffIcon: (kind: 'neg' | 'pos') => ({
    display: 'inline-block',
    fontStyle: 'normal',
    marginRight: 10,
    height: 22,
    width: 22,
    borderRadius: '50%',
    textAlign: 'center',
    fontSize: 16,
    lineHeight: '19px',
    fontWeight: '600',
    backgroundColor:
      kind === 'neg' ? 'rgba(232, 76, 133, 0.1)' : 'rgba(18, 196, 87, 0.1)',
  }),
});
